import React, { useMemo } from "react";
import { useDispatch } from "../redux/store";
import dayjs from "dayjs";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";
import { AppointmentState } from "../types";
import { sendToWindowDataLayer } from "../utils/dom";
import {
  SlotInfo,
  AvailableSlotsInterfaceV2,
} from "../types/available-slots.interface";
import {
  getShopNow,
  resolveSameDayBookings,
} from "../utils/sameDayBooking";

interface DatePickerProps {
  appointment: AppointmentState;
  selectedDate: string | null;
  setSelectedDate: (dateKey: string | null) => void;
  currentMonth: dayjs.Dayjs;
  onMonthChange: (newMonth: dayjs.Dayjs) => void;
  availabilityResponse?: AvailableSlotsInterfaceV2;
  loadingDates?: boolean;
}

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const getAvailableSlotsCount = (
  slotsArray: SlotInfo[] | undefined
): number => {
  if (!Array.isArray(slotsArray)) return 0;
  return slotsArray.filter((slot) => slot.isAvailable).length;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  appointment,
  selectedDate,
  setSelectedDate,
  currentMonth,
  onMonthChange,
  availabilityResponse,
  loadingDates,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const accentColor = appointment?.shopData?.color || "#2b73b9";
  const shopData = appointment?.shopData;
  const apptType = appointment?.apptType;
  const sameDayBookings = resolveSameDayBookings(shopData, apptType);

  const today = useMemo(
    () => getShopNow(shopData).startOf("day"),
    [shopData]
  );

  // The earliest bookable date
  const earliestDate = useMemo(
    () => (sameDayBookings ? today : today.add(1, "day")),
    [sameDayBookings, today]
  );

  // Build the calendar grid for the current month
  const calendarDays = useMemo(() => {
    const firstOfMonth = currentMonth.startOf("month");
    const daysInMonth = currentMonth.daysInMonth();
    const startDow = firstOfMonth.day(); // 0=Sun

    const cells: (dayjs.Dayjs | null)[] = [];

    // Leading empty cells
    for (let i = 0; i < startDow; i++) {
      cells.push(null);
    }

    // Actual day cells
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(firstOfMonth.date(d));
    }

    return cells;
  }, [currentMonth]);

  // Can we navigate to the previous month?
  const canGoPrev = currentMonth.isAfter(today.startOf("month"));

  const handlePrevMonth = () => {
    if (canGoPrev) {
      onMonthChange(currentMonth.subtract(1, "month"));
    }
  };

  const handleNextMonth = () => {
    onMonthChange(currentMonth.add(1, "month"));
  };

  const handleDayClick = (day: dayjs.Dayjs) => {
    const dateKey = day.format("YYYY-MM-DD");
    dispatch(
      updateAppointment({
        updates: { date: day.format("MM/DD/YYYY") },
      })
    );
    setSelectedDate(dateKey);
    sendToWindowDataLayer("dc-service-date", day.format("ddd DD MMM"));
  };

  // Check day availability from the response
  const getDayAvailability = (day: dayjs.Dayjs) => {
    const dateKey = day.format("YYYY-MM-DD");
    if (!availabilityResponse?.dates?.[dateKey]) return false;
    const dayData = availabilityResponse.dates[dateKey];
    const slotsArray =
      appointment.apptType === "wait"
        ? dayData.waiterSlots
        : dayData.dropOffSlots;
    return getAvailableSlotsCount(slotsArray) > 0;
  };

  return (
    <div
      id="date-picker"
      className="rounded-2xl bg-white p-5 font-roboto"
      style={{
        border: `2px solid ${accentColor}33`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {/* Month header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <h3
          className="text-lg font-bold"
          style={{ color: accentColor }}
        >
          {currentMonth.format("MMMM YYYY")}
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center justify-center w-9 h-9 rounded-full transition-opacity focus:outline-none"
            style={{
              border: "2px solid #d1d5db",
              backgroundColor: "transparent",
              cursor: canGoPrev ? "pointer" : "not-allowed",
              opacity: canGoPrev ? 1 : 0.4,
            }}
            onClick={handlePrevMonth}
            disabled={!canGoPrev}
            aria-label="Previous month"
          >
            <i
              className="fa-solid fa-chevron-left"
              aria-hidden="true"
              style={{
                fontSize: "14px",
                color: canGoPrev ? accentColor : "#9ca3af",
                lineHeight: 1,
              }}
            />
          </button>
          <button
            type="button"
            className="flex items-center justify-center w-9 h-9 rounded-full transition-opacity focus:outline-none"
            style={{
              border: "2px solid #d1d5db",
              backgroundColor: accentColor,
              cursor: "pointer",
            }}
            onClick={handleNextMonth}
            aria-label="Next month"
          >
            <i
              className="fa-solid fa-chevron-right"
              aria-hidden="true"
              style={{ fontSize: "14px", color: "#ffffff", lineHeight: 1 }}
            />
          </button>
        </div>
      </div>

      {loadingDates ? (
        <div className="flex flex-col items-center justify-center py-8">
          <i
            className="fa-solid fa-tire-rugged animate-spin text-5xl"
            aria-hidden="true"
            style={{ color: "#6b6b6b", lineHeight: 1 }}
          />
          <p className="text-sm text-gray-600 mt-3">
            Loading available dates...
          </p>
        </div>
      ) : (
        <>
          {/* Day-of-week header */}
          <div className="grid grid-cols-7 mb-1">
            {DAY_LABELS.map((label) => (
              <div
                key={label}
                className="text-center text-xs font-bold py-1"
                style={{ color: "#374151" }}
              >
                {label}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-y-1">
            {calendarDays.map((day, idx) => {
              if (!day) {
                // Empty leading cell
                return <div key={`empty-${idx}`} />;
              }

              const dateKey = day.format("YYYY-MM-DD");
              const isToday = day.isSame(today, "day");
              const isPast = day.isBefore(earliestDate, "day");
              const isSelected = selectedDate === dateKey;
              const isAvailable = !isPast && getDayAvailability(day);
              const isWeekend = day.day() === 0 || day.day() === 6;
              const isDisabled = isPast || !isAvailable;

              // Determine cell styles
              let bgColor = "transparent";
              let textColor = "#1f2937"; // gray-800
              let borderColor: string;
              let cursor = "pointer";
              let opacity = 1;

              if (isSelected) {
                bgColor = accentColor;
                textColor = "#ffffff";
                borderColor = accentColor;
              } else if (isDisabled) {
                bgColor = "#e5e7eb"; // gray-200
                textColor = "#9ca3af"; // gray-400
                borderColor = "transparent";
                cursor = "default";
                opacity = 0.7;
              } else {
                // Available / active day
                borderColor = accentColor;
                if (isWeekend) {
                  textColor = "#6b7280"; // gray-500
                }
              }

              return (
                <div key={dateKey} className="flex justify-center">
                  <button
                    type="button"
                    className="relative flex flex-col items-center justify-center rounded-lg transition-all font-roboto"
                    style={{
                      width: "42px",
                      height: "42px",
                      backgroundColor: bgColor,
                      color: textColor,
                      border: `2px solid ${borderColor}`,
                      cursor: isDisabled ? "default" : cursor,
                      opacity,
                      fontSize: "14px",
                      fontWeight: isSelected || isToday ? 700 : 500,
                    }}
                    onClick={() => {
                      if (!isDisabled) handleDayClick(day);
                    }}
                    disabled={isDisabled}
                    data-service-date={dateKey}
                  >
                    {day.date()}
                    {/* Today indicator dot */}
                    {isToday && !isSelected && (
                      <span
                        className="absolute bottom-1 w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: accentColor }}
                      />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
